import { HttpErrorResponse } from '@angular/common/http';
import { ICompanyDTO, ICompanyNameIdLegalFormDTO, ICompanyStatusDTO } from '@modeso/types__aumico-lib-companies-be';
import { Action, createAction, createActionGroup, emptyProps, props } from '@ngrx/store';
import { ICompanyExportDto } from '../dtos/company-export-dto.interface';
import { ICompanyExportTableHeaders } from '../dtos/company-export-table-header-dto.interface';
import { CompanyValuationRequestDTO } from '../dtos/company-valuation-request-dto.interface';
import { TasksUpdate } from '../enums/tasks-update.enum';

// **Create Company */
export const createCompany = createAction(
    '[Aumico-Companies] createCompany',
    // `activateFx` (REV-21512): the terms were accepted while creating. Activation is bound to a
    // company id, so the effect performs it right after the company exists.
    props<{ company: ICompanyDTO; logo: File | null; activateFx?: boolean }>(),
);
export const onCreateCompanySuccessfully = createAction(
    '[Aumico-Companies] onCreateCompanySuccessfully',
    props<{ payload: { company: ICompanyDTO } }>(),
);
export const onCreateCompanyFailed = createAction(
    '[Aumico-Companies] onCreateCompanyFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// **Update Company */
export const updateCompany = createAction(
    '[Aumico-Companies] updateCompany',
    props<{ company: ICompanyDTO; logo: File | null; tasksUpdate?: TasksUpdate }>(),
);
export const onUpdateCompanySuccessfully = createAction(
    '[Aumico-Companies] onUpdateCompanySuccessfully',
    props<{ payload: ICompanyDTO }>(),
);
export const onUpdateCompanyFailed = createAction(
    '[Aumico-Companies] onUpdateCompanyFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const bulkUpdateCompanies = createAction(
    '[Aumico-Companies] bulkUpdateCompanies',
    props<{ companies: ICompanyDTO[]; logo: File | null; tasksUpdate?: TasksUpdate }>(),
);
export const onBulkUpdateCompaniesSuccessfully = createAction(
    '[Aumico-Companies] onBulkUpdateCompaniesSuccessfully',
    props<{ payload: ICompanyDTO[] }>(),
);
export const onBulkUpdateCompaniesFailed = createAction(
    '[Aumico-Companies] onBulkUpdateCompaniesFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// **Change Company Status */
export const changeStatus = createAction('[Aumico-Companies] changeStatus', props<{ payload: ICompanyStatusDTO }>());
export const onChangeStatusSuccessfully = createAction(
    '[Aumico-Companies] changeStatusSuccessfully',
    props<{ payload: ICompanyDTO }>(),
);
export const onChangeStatusFailed = createAction(
    '[Aumico-Companies] changeStatusFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// **Delete Company */
export const deleteCompany = createAction('[Aumico-Companies] deleteCompany', props<{ payload: { _id: string } }>());
export const onDeleteCompanySuccessfully = createAction(
    '[Aumico-Companies] onDeleteCompanySuccessfully',
    props<{ payload: ICompanyDTO }>(),
);
export const onDeleteCompanyFailed = createAction(
    '[Aumico-Companies] onDeleteCompanyFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// **Get Companies */
export const getCompanies = createAction('[Aumico-Companies] getCompanies');
export const onGettingCompaniesSuccessfully = createAction(
    '[Aumico-Companies] onGettingCompaniesSuccessfully',
    props<{ payload: ICompanyDTO[] }>(),
);
export const onGettingCompaniesFailed = createAction(
    '[Aumico-Companies] onGettingCompaniesFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// **Get Companies name id legalForm */
export const getCompaniesNameIdLegalForm = createAction('[Aumico-Companies] getCompaniesNameIdLegalForm');
export const onGettingCompaniesNameIdLegalFormSuccessfully = createAction(
    '[Aumico-Companies] onGettingCompaniesNameIdLegalFormSuccessfully',
    props<{ payload: ICompanyNameIdLegalFormDTO[] }>(),
);
export const onGettingCompaniesNameIdLegalFormFailed = createAction(
    '[Aumico-Companies] onGettingCompaniesNameIdLegalFormFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const getCompanyById = createAction('[Aumico-Companies] getCompanyById', props<{ payload: string }>());
export const onGettingCompanyByIdSuccessfully = createAction(
    '[Aumico-Companies] onGettingCompanyByIdSuccessfully',
    props<{ payload: ICompanyDTO }>(),
);
export const onGettingCompanyByIdFailed = createAction(
    '[Aumico-Companies] onGettingCompanyByIdFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// **Update Company */
export const companyValuation = createAction(
    '[Aumico-Companies] companyValuation',
    props<{ payload: CompanyValuationRequestDTO[] }>(),
);
export const onCompanyValuationSuccess = createAction('[Aumico-Companies] onCompanyValuationSuccess');
export const onCompanyValuationFailure = createAction(
    '[Aumico-Companies] onCompanyValuationFailure',
    props<{ payload: HttpErrorResponse }>(),
);

export const downloadDefaultCompanyValuationFile = createAction(
    '[Aumico-Companies] downloadDefaultCompanyValuationFile',
);

export const onDownloadDefaultCompanyValuationFileSuccessfully = createAction(
    '[Aumico-Companies] onDownloadDefaultCompanyValuationFileSuccessfully',
    props<{ payload: any }>(),
);
export const onDownloadDefaultCompanyValuationFileFailed = createAction(
    '[Aumico-Companies] onDownloadDefaultCompanyValuationFileFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const resetCompanies = createAction('[Aumico-Companies] resetCompanies');
export const resetErrors = createAction('[Aumico-Companies] resetErrors');

export const getCompanyLogo = createActionGroup({
    source: '[Aumico-Companies] Get Company Logo',
    events: {
        download: props<{ documentId: string; documentName?: string }>(),
        downloadSuccess: props<{ payload: Blob }>(),
        downloadFailed: props<{ payload: HttpErrorResponse }>(),
    },
});

export const exportCompaniesDataAsExcel = createActionGroup({
    source: '[Aumico-Companies] Export Companies Data As Excel',
    events: {
        do: props<{
            payload: { tableHeaders: ICompanyExportTableHeaders; companies: ICompanyExportDto[]; fileName: string };
        }>(),
        success: emptyProps(),
        failed: props<{ payload: HttpErrorResponse }>(),
    },
});

export interface ActionWithPayload<T> extends Action {
    payload: T;
}
